import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/pages/home/components/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function Category() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const genre = params.get('genre') || '';
  const [sort, setSort] = useState('default');

  const list = useMemo(() => {
    let arr = [...comics];
    if (genre) arr = arr.filter((c) => c.genre === genre);
    if (q) arr = arr.filter((c) => c.title.includes(q) || c.genre.includes(q) || c.tagline.includes(q));
    if (sort === 'rating') arr.sort((a, b) => b.rating - a.rating);
    if (sort === 'views') arr.sort((a, b) => parseFloat(b.views) - parseFloat(a.views));
    return arr;
  }, [q, genre, sort]);

  const setGenre = (g: string) => {
    const next = new URLSearchParams(params);
    if (g) next.set('genre', g); else next.delete('genre');
    setParams(next, { replace: true });
  };

  const setKeyword = (kw: string) => {
    const next = new URLSearchParams(params);
    if (kw) next.set('q', kw); else next.delete('q');
    setParams(next, { replace: true });
  };

  const pageTitle = q ? `搜索“${q}”` : genre || '全部韩漫';

  return (
    <>
      <Seo
        title={`${pageTitle} - 韩国漫画大全分类榜单`}
        description={`浏览${pageTitle}相关韩国漫画，每日更新、连载进度同步，最新韩漫新作第一时间收录。`}
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `韩国漫画大全 · ${pageTitle}`,
            inLanguage: 'zh-CN',
          },
        ]}
      />
      <Navbar />

      <main className="mx-auto max-w-7xl px-4 pb-16 pt-24 md:px-6 md:pb-24 md:pt-32">
        {/* Breadcrumb */}
        <nav aria-label="面包屑" className="mb-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="text-foreground-800">分类榜单</span>
          {genre && (
            <>
              <i className="ri-arrow-right-s-line text-foreground-300"></i>
              <span className="text-primary-600">{genre}</span>
            </>
          )}
        </nav>

        <div className="mb-8">
          <span className="text-xs font-medium tracking-wide text-primary-600">CATEGORY</span>
          <h1 className="mt-1 font-heading text-3xl text-foreground-950 md:text-4xl">{pageTitle}韩漫</h1>
          <p className="mt-2 text-sm text-foreground-600">
            共 <strong className="text-primary-600">{list.length}</strong> 部作品 · 每日更新不停歇，连载进度实时同步
          </p>
        </div>

        {/* Search + sort */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <form
            onSubmit={(e) => { e.preventDefault(); setKeyword((e.currentTarget.elements.namedItem('kw') as HTMLInputElement).value); }}
            className="flex flex-1 items-center gap-2 rounded-full border border-background-200 bg-background-50 px-3 py-2.5 focus-within:ring-2 focus-within:ring-primary-300"
          >
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              name="kw"
              type="search"
              defaultValue={q}
              placeholder="在当前分类中搜索韩漫"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              aria-label="搜索漫画"
            />
            <button type="submit" className="whitespace-nowrap rounded-full bg-primary-500 px-4 py-1.5 text-sm text-white transition-colors hover:bg-primary-600">
              搜索
            </button>
          </form>
          <div className="flex items-center gap-2">
            <label className="text-sm text-foreground-600">排序</label>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-full border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none focus:ring-2 focus:ring-primary-300"
            >
              <option value="default">默认排序</option>
              <option value="rating">评分最高</option>
              <option value="views">人气最高</option>
            </select>
          </div>
        </div>

        {/* Genre chips */}
        <div className="mb-8 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setGenre('')}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
              !genre ? 'bg-primary-500 font-medium text-white' : 'bg-background-100 text-foreground-700 hover:text-primary-600'
            }`}
          >
            全部
          </button>
          {categories.map((c) => (
            <button
              key={c.name}
              type="button"
              onClick={() => setGenre(c.name)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                genre === c.name ? 'bg-primary-500 font-medium text-white' : 'bg-background-100 text-foreground-700 hover:text-primary-600'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* Grid */}
        {list.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {list.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/50 py-20 text-center">
            <i className="ri-inbox-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-base font-medium text-foreground-800">没有找到相关作品</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词或分类再试试吧</p>
            <button
              type="button"
              onClick={() => { setKeyword(''); setGenre(''); }}
              className="mt-5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              清除筛选
            </button>
          </div>
        )}

        {/* Pager hint */}
        {list.length > 12 && (
          <div className="mt-10 flex items-center justify-center">
            <button type="button" className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600">
              加载更多
            </button>
          </div>
        )}
      </main>

      <Footer />
    </>
  );
}